import axios, { AxiosError, AxiosResponse } from "axios";
import { useQuery } from "react-query";
export enum InstatusStatus {
  UP = "UP",
  HASISSUES = "HASISSUES",
  UNDERMAINTENANCE = "UNDERMAINTENANCE",
}
type InstatusPublicSummaryResponse = {
  page: {
    name: string;
    url: string;
    status: InstatusStatus;
  };
  activeIncidents: Record<string, string>[];
  activeMaintenances: Record<string, string>[];
};
type InstatusComponent = {
  id: string;
  name: string;
  status: string;
};
type InstatusComponentsResponse = {
  components: InstatusComponent[];
};

// Instatus's aggregate page status can lag behind per-component statuses
// (e.g. after a synced third-party outage clears), so a non-UP aggregate is
// only trusted when at least one component is actually non-operational.
export function resolveInstatusStatus(status: InstatusStatus | undefined, components: InstatusComponent[] | undefined): InstatusStatus | undefined {
  if (status === undefined || status === InstatusStatus.UP) {
    return status;
  }
  if (components?.length && components.every(component => component.status === "OPERATIONAL")) {
    return InstatusStatus.UP;
  }
  return status;
}
type UseInstatusData = {
  status: InstatusStatus | undefined;
};
export function useInstatus(): UseInstatusData {
  const api = axios.create({
    baseURL: `https://status.blacksmith.sh`,
    timeout: 60000
  });
  const {
    data
  } = useQuery(["status"], async () => {
    const response = await api.get<any, AxiosResponse<InstatusPublicSummaryResponse>>("/v3/summary.json");
    const status = response?.data?.page?.status;
    if (status === undefined || status === InstatusStatus.UP) {
      return status;
    }
    let components: InstatusComponent[] | undefined;
    try {
      const componentsResponse = await api.get<any, AxiosResponse<InstatusComponentsResponse>>("/v2/components.json");
      components = componentsResponse?.data?.components;
    } catch {
      // Fall back to the aggregate status if components can't be fetched.
    }
    return resolveInstatusStatus(status, components);
  }, {
    suspense: true,
    refetchInterval: 10000,
    useErrorBoundary: (error: AxiosError) => error?.response?.status !== 404
  });
  return {
    status: data
  };
}